<template>
  <div v-if="visible" class="fixed inset-0 z-[987654321000] flex items-center justify-center p-4">
 
    <div 
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" 
      @click="close"
    ></div>

 
    <div class="relative w-full max-w-[1000px] transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[85vh]">
      
 
      <div class="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
        <span class="text-lg font-semibold text-slate-800">Task Progress</span>
        <button 
          class="rounded-full p-3 w-5 h-5 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors focus:outline-none" 
          @click="close"
        >
          <i class="fa-solid fa-xmark text-lg"></i>
        </button>
      </div>

 
      <div class="overflow-y-auto p-6 space-y-4">
        <div 
          v-if="selectedCampaign" 
          class="rounded-xl border border-slate-100 p-3 bg-slate-50/50"
          :class="selectedCampaign?.background"
        >
 
          <div class="mb-3">
            <div 
              v-if="selectedCampaign.mode === 'tournament'" 
              class="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600 bg-white p-3 rounded-lg border border-slate-100"
            >
              <span class="flex items-center gap-1.5">
                <i class="fa-solid fa-money-bill text-emerald-500"></i> Total Points:
                <strong class="font-bold text-slate-900">{{ selectedCampaign.points }}</strong>
              </span>
              <span v-if="selectedCampaign?.reached_at" class="text-xs text-slate-400">
                Last updated: <strong class="font-medium text-slate-600">{{ formatDate(selectedCampaign?.reached_at) }}</strong>
              </span>
            </div>

            <div v-else class="space-y-2 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
              <div class="flex items-center justify-between text-sm font-medium text-slate-700">
                <span>Campaign Progress</span>
                <span class="text-blue-600 font-bold">{{ campaignProgress.toFixed(2) }}%</span>
              </div>
              <div class="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div 
                  class="h-full rounded-full bg-blue-500 transition-all duration-500 ease-out" 
                  :style="{ width: campaignProgress + '%' }"
                ></div>
              </div>
            </div>
          </div>

 
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div 
              v-for="(task, index) in selectedCampaign.tasks" 
              :key="index" 
              class="flex flex-col gap-3 rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition-all"
              :class="{ 'border-emerald-200 bg-emerald-50/20': isTaskCompleted(task) }"
            >
 
              <div class="flex items-start gap-3">
                <div 
                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm transition-colors"
                  :class="isTaskCompleted(task) ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'"
                >
                  <i :class="getTaskIcon(task.type)"></i>
                </div>
                
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2">
                    <span class="truncate text-sm font-semibold text-slate-800">
                      {{ getLabelTask(task.type) }}
                    </span>
                    <span class="shrink-0 text-xs font-mono font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">
                      {{ getPlayerValue(task) }} / {{ task.value }}{{ getSuffix(task.type) }}
                    </span>
                  </div>

 
                  <div class="mt-1">
                    <div v-if="showMinMax(task)" class="text-xs text-slate-400">
                      <span>Min Bet: <strong class="font-medium text-slate-600">{{ formatBet(task.min_bet) }}</strong></span>
                      <span class="mx-1.5 text-slate-300">|</span>
                      <span>Max Bet: <strong class="font-medium text-slate-600">{{ formatBet(task.max_bet) }}</strong></span>
                    </div>

                    <div v-else-if="task.type === 'catch_multipliers'" class="text-xs text-blue-600 font-medium">
                      Cashout {{ task.multiplier_reached }}X to complete the task once.
                    </div>
                  </div>
                </div>
              </div>

 
              <div v-if="selectedCampaign?.mode !== 'tournament'" class="space-y-1.5">
                <div class="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div 
                    class="h-full rounded-full transition-all duration-500 ease-out"
                    :class="isTaskCompleted(task) ? 'bg-emerald-500' : 'bg-blue-500'" 
                    :style="{ width: getTaskProgress(task) + '%' }"
                  ></div>
                </div>
                <div class="flex items-center justify-end gap-1 text-right text-xs font-medium">
                  <span :class="isTaskCompleted(task) ? 'text-emerald-600' : 'text-slate-500'">
                    {{ getTaskProgress(task).toFixed(2) }}%
                  </span>
                  <i v-if="isTaskCompleted(task)" class="fa-solid fa-circle-check text-emerald-500 text-sm ml-0.5"></i>
                </div>
              </div>

 
              <div 
                v-else 
                class="flex items-center gap-1.5 text-xs font-medium text-slate-500 pt-2 border-t border-slate-50"
              >
                <i class="fa-solid fa-money-bill text-slate-400"></i> Points: 
                <span class="text-slate-800 font-bold">{{ selectedCampaign?.tasks[index]?.points ?? 0 }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: "TaskProgress",
  props: {
    visible: Boolean,
    tasksData: Object,
    showTask: Function,
  },
  computed: {
    selectedCampaign() {
      return this.tasksData;
    },
    campaignProgress() {
      if (!this.selectedCampaign?.tasks?.length) return 0;
      const progresses = this.selectedCampaign.tasks.map((task) =>
        this.getTaskProgress(task)
      );
      return (
        progresses.reduce((sum, val) => sum + val, 0) /
        this.selectedCampaign.tasks.length
      );
    },
  },
  methods: {
    close() {
      this.showTask(null, false);
    },
    getTaskIcon(type) {
      const icons = {
        bet_total: "fa-solid fa-dollar-sign",
        collect_multipliers: "fa-solid fa-chart-line",
        play_rounds: "fa-solid fa-bullseye",
        win_money: "fa-solid fa-dollar-sign",
        win_rounds: "fa-solid fa-trophy",
        catch_multipliers: "fa-solid fa-burst",
      };
      return icons[type] || "fa-solid fa-bullseye";
    },
    getLabelTask(type) {
      return (
        {
          bet_total: "Total Bets",
          collect_multipliers: "Total Multipliers",
          play_rounds: "Total Rounds",
          win_money: "Total Win Amount",
          win_rounds: "Total Win Rounds",
          catch_multipliers: "Caught Multipliers",
        }[type] || ""
      );
    },
    getSuffix(type) {
      const c = this.selectedCampaign;
      return (
        {
          bet_total: c.currency,
          collect_multipliers: "X",
          play_rounds: "",
          win_money: c.currency,
          win_rounds: "",
          catch_multipliers: "X",
        }[type] || ""
      );
    },
    getPlayerValue(task) {
      // Ottimizzato leggendo direttamente il valore del task passato senza usare .find() basato sul tipo
      return task?.value_player ?? 0;
    },
    getTaskProgress(task) {
      // Ottimizzato per evitare problemi di calcolo in caso di task multipli dello stesso tipo
      const playerValue = task?.value_player ?? 0;
      const required = task.value ?? 0;
      if (!required) return 100;
      const perc = (Number(playerValue) / Number(required)) * 100;
      return Math.max(0, Math.min(100, perc));
    },
    isTaskCompleted(task) {
      return !!task?.is_completed;
    },
    showMinMax(task) {
      return (
        task.type !== "catch_multipliers" &&
        (parseFloat(task.min_bet) > 0 || parseFloat(task.max_bet) > 0)
      );
    },
    formatBet(value) {
      return parseFloat(value) > 0 ? parseFloat(value).toFixed(2) : "Not set";
    },
    formatDate(dateToFormat) {
      const date = new Date(dateToFormat);

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    },
  },
};
</script>